import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Combobox } from "../../../components/ui";
import { useUpdateShipment } from "../../../features/shipments/mutations";
import { isShipmentUnseenByDriver, type Shipment } from "../../../features/shipments/types";
import { useVehiclesSocketStore } from "../../../store/admin/vehiclesSocketStore";
import { buildUnitOptions, shipmentUpdateErrorKey } from "../utils/shipmentUnit";

interface ShipmentUnitEditorProps {
  shipment: Shipment;
}

export default function ShipmentUnitEditor({ shipment }: ShipmentUnitEditorProps) {
  const { t } = useTranslation("envios");
  const vehicles = useVehiclesSocketStore((state) => state.vehicles);
  const updateShipment = useUpdateShipment();

  const currentDeviceId = shipment.actual_device_id ?? null;
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(currentDeviceId);

  useEffect(() => {
    setSelectedDeviceId(currentDeviceId);
  }, [shipment.id, currentDeviceId]);

  const options = useMemo(() => buildUnitOptions(vehicles), [vehicles]);
  const selected = options.find((option) => option.value === selectedDeviceId) ?? null;
  const lockedByTrip = !!shipment.trip_id;
  const dirty = selectedDeviceId !== currentDeviceId;

  const save = () =>
    updateShipment.mutate(
      { id: shipment.id, data: { actual_device_id: selectedDeviceId } },
      {
        onSuccess: () =>
          toast.success(t(selectedDeviceId ? "unitEditor.saved" : "unitEditor.removed")),
        onError: (error) => {
          setSelectedDeviceId(currentDeviceId);
          toast.error(t(`updateErrors.${shipmentUpdateErrorKey(error)}`));
        },
      },
    );

  return (
    <div className="rounded-2xl border border-line p-5">
      <div className="mb-4 flex items-center gap-2">
        <i className="ri-truck-line text-lg text-content" />
        <h3 className="text-sm font-bold text-content">{t("unitEditor.title")}</h3>
      </div>

      {lockedByTrip && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          <i className="ri-route-line mt-0.5" />
          <p>{t("unitEditor.tripLocked")}</p>
        </div>
      )}

      {isShipmentUnseenByDriver(shipment) && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          <i className="ri-eye-off-line mt-0.5" />
          <p>
            {t(
              shipment.trip_unit_state === "closed_only"
                ? "tripPicker.closedWarningShort"
                : "tripPicker.noneWarningShort",
            )}
          </p>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-content">
            {t("unitEditor.label")}
          </label>
          <Combobox
            value={selected}
            onChange={(option) => setSelectedDeviceId(option?.value ?? null)}
            options={options}
            placeholder={t("unitEditor.placeholder")}
            isClearable
            isDisabled={lockedByTrip || updateShipment.isPending}
            aria-label={t("unitEditor.label")}
          />
        </div>

        <Button
          size="sm"
          loading={updateShipment.isPending}
          disabled={lockedByTrip || !dirty}
          icon={<i className="ri-check-line" />}
          onClick={save}
        >
          {t("unitEditor.save")}
        </Button>
      </div>
    </div>
  );
}
